import React from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Clock, Star } from 'lucide-react';

const ListingCard = ({ listing, variant = 'default', showFeaturedBadge = true }) => {
  const navigate = useNavigate();
  const isFeatured = !!listing?.featured;

  const formatDate = (isoDate) => {
    const date = new Date(isoDate);
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  if (variant === 'featured') {
    return (
      <div
        className="rounded-xl shadow-sm hover:shadow-md transition-all cursor-pointer p-7 relative border"
        style={{
          backgroundColor: '#FFFFFF',
          borderColor: 'rgba(212,130,10,0.35)'
        }}
        onClick={() => navigate(`/listing/${listing.id}`)}
        data-testid={`featured-card-${listing.id}`}
      >
        {showFeaturedBadge && (
          <div
            className="absolute top-4 right-4 flex items-center gap-1 px-2 py-0.5 rounded-full"
            style={{ backgroundColor: 'rgba(212,130,10,0.12)' }}
          >
            <Star className="w-3 h-3" style={{ fill: '#D4820A', color: '#D4820A' }} strokeWidth={1.5} />
            <span className="text-[10px] uppercase tracking-[0.15em] font-medium" style={{ color: '#D4820A' }}>
              Featured
            </span>
          </div>
        )}
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[10px] uppercase tracking-[0.15em] font-semibold rounded-full px-3 py-1" style={{ backgroundColor: '#0F2942', color: '#FFFFFF' }}>
              {listing.opportunity_type}
            </span>
            <span className="text-[10px] uppercase tracking-[0.15em] font-medium rounded-full px-3 py-1" style={{ backgroundColor: 'rgba(212,130,10,0.15)', color: '#D4820A' }}>
              {listing.industry}
            </span>
          </div>
          <h3 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#0F2942' }}>
            {listing.title}
          </h3>
          <p className="text-sm font-medium" style={{ color: '#0F2942' }}>{listing.organisation}</p>
          <div className="flex flex-wrap items-center gap-4 text-sm" style={{ color: '#0F2942' }}>
            <div className="flex items-center gap-1.5">
              <MapPin className="w-3.5 h-3.5" strokeWidth={1.5} />
              <span>{listing.city}, {listing.country}</span>
            </div>
            {listing.salary && <span className="font-medium" style={{ color: '#0F2942' }}>{listing.salary}</span>}
            <div className="flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5" strokeWidth={1.5} />
              <span>Closes {formatDate(listing.closing_date)}</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (variant === 'compact') {
    return (
      <div
        className="bg-white rounded-xl shadow-sm hover:shadow-md transition-all cursor-pointer p-5 border border-transparent hover:border-[#D4820A]/30"
        onClick={() => navigate(`/listing/${listing.id}`)}
        data-testid={`listing-card-${listing.id}`}
      >
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[10px] uppercase tracking-[0.15em] font-semibold rounded-full px-2.5 py-0.5" style={{ backgroundColor: '#0F2942', color: '#FFFFFF' }}>
              {listing.opportunity_type}
            </span>
            <span className="text-[10px] uppercase tracking-[0.15em] font-medium rounded-full px-2.5 py-0.5" style={{ backgroundColor: 'rgba(212,130,10,0.15)', color: '#D4820A' }}>
              {listing.industry}
            </span>
          </div>
          <h3 className="text-xl font-semibold tracking-tight" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#0F2942' }}>
            {listing.title}
          </h3>
          <p className="text-sm font-medium" style={{ color: '#0F2942' }}>{listing.organisation}</p>
          <div className="flex items-center justify-between text-xs" style={{ color: '#0F2942' }}>
            <div className="flex items-center gap-1.5">
              <MapPin className="w-3.5 h-3.5" strokeWidth={1.5} />
              <span>{listing.city}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5" strokeWidth={1.5} />
              <span>Closes {formatDate(listing.closing_date)}</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="rounded-xl shadow-sm hover:shadow-md transition-all cursor-pointer p-6 border"
      style={{
        backgroundColor: '#FFFFFF',
        borderColor: isFeatured ? 'rgba(212,130,10,0.35)' : 'transparent'
      }}
      onClick={() => navigate(`/listing/${listing.id}`)}
      data-testid={`listing-card-${listing.id}`}
    >
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[10px] uppercase tracking-[0.15em] font-semibold rounded-full px-3 py-1" style={{ backgroundColor: '#0F2942', color: '#FFFFFF' }} data-testid="card-opportunity-type">
            {listing.opportunity_type}
          </span>
          <span className="text-[10px] uppercase tracking-[0.15em] font-medium rounded-full px-3 py-1" style={{ backgroundColor: 'rgba(212,130,10,0.15)', color: '#D4820A' }} data-testid="card-industry">
            {listing.industry}
          </span>
          {isFeatured && (
            <span
              className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] font-medium rounded-full px-2.5 py-0.5"
              style={{ backgroundColor: 'rgba(212,130,10,0.12)', color: '#D4820A' }}
              data-testid="card-featured-label"
            >
              <Star className="w-3 h-3" style={{ fill: '#D4820A', color: '#D4820A' }} strokeWidth={1.5} />
              Featured
            </span>
          )}
          {listing.tags && listing.tags.length > 0 && listing.tags.slice(0, 2).map((tag) => (
            <span key={tag} className="text-[10px] tracking-wide rounded-full px-2.5 py-0.5" style={{ backgroundColor: '#F0EBE2', color: '#0F2942' }}>
              {tag}
            </span>
          ))}
        </div>
        <h3 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#0F2942' }}>
          {listing.title}
        </h3>
        <div className="space-y-1.5">
          <p className="text-base font-medium" style={{ color: '#0F2942' }}>{listing.organisation}</p>
          <div className="flex flex-wrap items-center gap-4 text-sm" style={{ color: '#0F2942' }}>
            <div className="flex items-center gap-1.5">
              <MapPin className="w-4 h-4" strokeWidth={1.5} />
              <span>{listing.city}, {listing.country}</span>
            </div>
          </div>
          {listing.salary && (
            <p className="text-sm font-medium" style={{ color: '#0F2942' }}>{listing.salary}</p>
          )}
        </div>
        <div className="flex items-center justify-between pt-4" style={{ borderTop: '1px solid #F0EBE2' }}>
          <div className="flex items-center gap-1.5 text-xs" style={{ color: '#0F2942' }}>
            <Clock className="w-4 h-4" strokeWidth={1.5} />
            <span>Closes {formatDate(listing.closing_date)}</span>
          </div>
          <span className="text-sm font-medium hover:underline" style={{ color: '#0F2942' }}>View Details &rarr;</span>
        </div>
      </div>
    </div>
  );
};

export default ListingCard;
